import { useContext, useEffect, useMemo, useState } from "react";

import { getReferenceString } from "@medplum/core";
import { Observation } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Drawer, Flex, Stack, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { Loading } from "@/components/Loading";
import ScrollableTable from "@/components/ScrollableTable";
import { TextWrapper } from "@/components/TextWrapper";
import { ProfileContext } from "@/contexts/profile";
import { ObservationCategory } from "@/fhir/enums/fhir-valuesets";
import { Systems } from "@/fhir/enums/systems";
import { filterObservationsFromTerminalLabOrders } from "@/health-gorilla/services/lab-result-trends";
import {
  getLabObservationResultDisplay,
  getLabObservationResultInterpretation,
} from "@/health-gorilla/utils/labs";
import { longUSDateTimeFormat } from "@/utils/datetime";
import {
  getCodeableConceptDisplay,
  getResourcesFromBundle,
  joinTokenString,
} from "@/utils/medplum";

import LabOrderResultsTrendGraph from "./LabOrderResultsTrendGraph";

interface LabOrderResultsTrendDrawerProps {
  opened: boolean;
  onClose: () => void;
  result: Observation | undefined;
}

interface LabOrderResultTrendTableRowProps {
  result: Observation;
  rowIndex: number;
}

export default function LabOrderResultsTrendDrawer({
  opened,
  onClose,
  result,
}: LabOrderResultsTrendDrawerProps) {
  const medplum = useMedplum();
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [allResults, setAllResults] = useState<Observation[]>([]);

  useEffect(() => {
    startLoading();
    (async () => {
      try {
        if (!result?.subject) return;
        const coding = result.code?.coding?.[0];

        if (!coding?.system || !coding?.code) return;

        const { bundle } = await medplum.searchResources("Observation", {
          category: joinTokenString(Systems.HL7ObservationCategory, ObservationCategory.Laboratory),
          subject: getReferenceString(result.subject),
          code: joinTokenString(coding.system, coding.code),
          _sort: "-date,-_lastUpdated",
          _count: 1000,
        });
        const allResults = getResourcesFromBundle("Observation", bundle);
        const filteredResults = await filterObservationsFromTerminalLabOrders(medplum, allResults);
        setAllResults(filteredResults);
      } finally {
        stopLoading();
      }
    })();
  }, [medplum, result, startLoading, stopLoading]);

  const resultDisplay = useMemo(
    () => (result ? getCodeableConceptDisplay(result.code) : ""),
    [result],
  );

  const shouldShowGraph = useMemo(
    () => allResults.length > 1 && result?.valueQuantity !== undefined,
    [allResults, result],
  );

  return (
    <Drawer.Root
      trapFocus
      size={640}
      opened={opened}
      position="right"
      onClose={onClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
      data-testid="lab-order-results-trend-drawer"
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header h={64} px="md" bg="gray.0">
          <Flex w="100%" gap="md" align="center" justify="space-between">
            <Flex gap="md" align="center" style={{ flex: 1 }}>
              <Drawer.CloseButton
                data-testid="lab-order-results-trend-close"
                aria-label="Close Lab OrderResult Trend Drawer"
              />
              <Drawer.Title style={{ flex: 1 }}>
                <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                  {`${resultDisplay} History`}
                </TextWrapper>
              </Drawer.Title>
            </Flex>
          </Flex>
        </Drawer.Header>
        <Drawer.Body p="lg">
          {isLoading && <Loading height={128} />}
          {!isLoading && (
            <Stack gap="lg">
              {shouldShowGraph && (
                <LabOrderResultsTrendGraph results={allResults} display={resultDisplay} />
              )}
              <ScrollableTable
                verticalSpacing="md"
                horizontalSpacing="lg"
                scrollProps={{ mt: "xs", mb: 0 }}
              >
                <Table.Thead c="cream.6" bg="cream.2">
                  <Table.Tr>
                    <Table.Th colSpan={8}>Date</Table.Th>
                    <Table.Th ta="right" colSpan={4}>
                      Value
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {allResults.map((observation, index) => (
                    <LabOrderResultTrendTableRow
                      rowIndex={index}
                      key={observation.id}
                      result={observation}
                    />
                  ))}
                </Table.Tbody>
              </ScrollableTable>
            </Stack>
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}

function LabOrderResultTrendTableRow({ result, rowIndex }: LabOrderResultTrendTableRowProps) {
  const { isPatient } = useContext(ProfileContext);
  const dateTime = useMemo(() => {
    return longUSDateTimeFormat(result.effectiveDateTime ?? result.meta?.lastUpdated);
  }, [result]);

  const [value, props] = useMemo(() => {
    const display = getLabObservationResultDisplay(result);
    const unit = result.valueQuantity?.unit;

    if (display && unit) return [`${display} ${unit}`, {}];
    if (display) return [display, {}];

    const [interpretation, interpretationColor] = getLabObservationResultInterpretation(
      result,
      isPatient,
    );
    return [interpretation, { c: interpretationColor, fw: "bold" }];
  }, [result, isPatient]);

  return (
    <Table.Tr key={result.id} data-testid={`lab-result-trend-row-${rowIndex}`}>
      <Table.Td colSpan={8}>{dateTime}</Table.Td>
      <Table.Td ta="right" colSpan={4} {...props}>
        {value}
      </Table.Td>
    </Table.Tr>
  );
}
